import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { getGatePass, markPrinted } from "../lib/api";
import { Printer, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import QRCode from "react-qr-code";

const PIMS_LOGO = "https://customer-assets.emergentagent.com/job_gpass-approval-hub/artifacts/aqa37c60_pims%20logo.png";

const PrintGatePass = () => {
  const { passId } = useParams();
  const navigate = useNavigate();
  const [pass, setPass] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPass();
  }, [passId]);

  const loadPass = async () => {
    try {
      const { data } = await getGatePass(passId);
      if (data.status !== "approved" && data.status !== "gate_verified") {
        toast.error("Gate pass is not approved yet");
        navigate(-1);
        return;
      }
      setPass(data);
    } catch (error) {
      toast.error("Failed to load gate pass");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try { await markPrinted(passId); } catch {}
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!pass) return null;

  const fmtDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const getApprovalByStage = (stage) => pass.approval_history?.find(h => h.stage?.includes(stage));

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header - No Print */}
      <div className="no-print bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-between items-center">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="flex gap-2">
            <Button size="sm" onClick={handlePrint} data-testid="print-btn">
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
            <Button size="sm" variant="outline" onClick={handlePrint} data-testid="reprint-btn">
              <Printer className="h-4 w-4 mr-2" /> Reprint
            </Button>
          </div>
        </div>
      </div>

      {/* Printable Content - A5 Landscape */}
      <div className="flex justify-center p-4">
        <div className="bg-white gate-pass-print" data-testid="gate-pass-printable" style={{ width: "210mm", minHeight: "148mm", maxHeight: "148mm" }}>
          <div className="gate-pass-ticket p-4" style={{ fontSize: "10px" }}>
            {/* Header Row */}
            <div className="flex items-center justify-between border-b border-slate-300 pb-2 mb-3">
              <img src={PIMS_LOGO} alt="PIMS" className="h-12" />
              <div className="text-center flex-1">
                <h1 className="text-base font-bold text-slate-900 uppercase tracking-wide">Gate Pass</h1>
                <p className="text-[9px] text-slate-500">Prem Institute of Medical Sciences</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] text-slate-400">Pass No.</p>
                <p className="font-mono font-bold text-xs">{pass.request_number}</p>
              </div>
            </div>

            {pass.status === "gate_verified" && (
              <div className="text-center mb-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded text-[10px] font-semibold">
                  <ShieldCheck className="h-3 w-3" /> GATE VERIFIED - EXIT ALLOWED
                </span>
              </div>
            )}

            {/* Main Content - 3 columns */}
            <div className="flex gap-3 mb-3">
              {/* Student Photo */}
              {pass.student_photo && (
                <div className="flex-shrink-0 w-20">
                  <img 
                    src={pass.student_photo} 
                    alt="Student" 
                    className="w-20 h-24 object-cover rounded border border-slate-300" 
                    data-testid="student-photo-print"
                  />
                </div>
              )}

              {/* Student Details */}
              <div className="flex-1 space-y-1">
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Student Name</p>
                    <p className="font-semibold text-xs">{pass.student_name}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Father's Name</p>
                    <p className="font-semibold text-xs">{pass.parent_name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Reg. No</p>
                    <p className="font-medium text-[10px]">{pass.student_enrollment || "-"}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Course / Year</p>
                    <p className="font-medium text-[10px]">{pass.course_name} - Year {pass.year}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Student Mobile</p>
                    <p className="font-mono text-[10px]">{pass.student_phone || pass.emergency_contact || "-"}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Parent Contact</p>
                    <p className="font-mono text-[10px]">{pass.parent_phone || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Pass Details */}
              <div className="flex-1 border-l border-r border-slate-200 px-3 space-y-1">
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Pass Type</p>
                    <p className="font-medium text-[10px] capitalize">{pass.pass_type?.replace(/_/g, " ")}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Destination</p>
                    <p className="font-medium text-[10px]">{pass.destination}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Exit Date & Time</p>
                    <p className="font-medium text-[10px]">{fmtDate(pass.exit_date)} {pass.exit_time}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-slate-400 uppercase">Return Date & Time</p>
                    <p className="font-medium text-[10px]">{fmtDate(pass.expected_return_date)} {pass.expected_return_time || ""}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[8px] text-slate-400 uppercase">Reason</p>
                    <p className="font-medium text-[10px]">{pass.reason}</p>
                  </div>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex flex-col items-center justify-center w-24">
                <QRCode value={`PIMS-GP:${pass.request_number}`} size={70} level="M" data-testid="qr-code" />
                <p className="text-[7px] text-slate-400 mt-1">Scan to verify</p>
              </div>
            </div>

            {/* Approval Row */}
            <div className="border-t border-dashed border-slate-300 pt-2 mb-2">
              <p className="text-[8px] font-semibold text-slate-500 uppercase mb-1">Approval Details</p>
              <div className="grid grid-cols-3 gap-2 text-[9px]">
                <div className="border border-slate-200 rounded p-1.5">
                  <p className="text-[7px] text-slate-400">Class Coordinator</p>
                  {getApprovalByStage("coordinator") ? (
                    <>
                      <p className="font-medium text-emerald-600">{getApprovalByStage("coordinator")?.approver_name}</p>
                      <p className="text-[7px] text-slate-400">
                        {getApprovalByStage("coordinator")?.timestamp ? new Date(getApprovalByStage("coordinator").timestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                      </p>
                    </>
                  ) : getApprovalByStage("vp_as_coordinator") ? (
                    <>
                      <p className="font-medium text-emerald-600">{getApprovalByStage("vp_as_coordinator")?.approver_name}</p>
                      <p className="text-[7px] text-amber-600">(VP as Coordinator)</p>
                    </>
                  ) : <p className="text-slate-400">-</p>}
                </div>
                <div className="border border-slate-200 rounded p-1.5">
                  <p className="text-[7px] text-slate-400">Principal</p>
                  {getApprovalByStage("principal") && !getApprovalByStage("principal")?.stage?.includes("vp") ? (
                    <>
                      <p className="font-medium text-emerald-600">{getApprovalByStage("principal")?.approver_name}</p>
                      <p className="text-[7px] text-slate-400">
                        {getApprovalByStage("principal")?.timestamp ? new Date(getApprovalByStage("principal").timestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                      </p>
                    </>
                  ) : getApprovalByStage("vp_as_principal") ? (
                    <>
                      <p className="font-medium text-emerald-600">{getApprovalByStage("vp_as_principal")?.approver_name}</p>
                      <p className="text-[7px] text-amber-600">(VP as Principal)</p>
                    </>
                  ) : <p className="text-slate-400">-</p>}
                </div>
                <div className="border border-slate-200 rounded p-1.5">
                  <p className="text-[7px] text-slate-400">Hostel Warden</p>
                  {getApprovalByStage("hostel_warden") ? (
                    <>
                      <p className="font-medium text-emerald-600">{getApprovalByStage("hostel_warden")?.approver_name}</p>
                      <p className="text-[7px] text-slate-400">
                        {getApprovalByStage("hostel_warden")?.timestamp ? new Date(getApprovalByStage("hostel_warden").timestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                      </p>
                    </>
                  ) : <p className="text-slate-400">-</p>}
                </div>
              </div>
            </div>

            {/* Parent Call Summary */}
            {pass.parent_call_data && (
              <div className="border-t pt-1.5 mb-2">
                <p className="text-[8px] font-semibold text-slate-500 uppercase mb-1">Parent Call</p>
                <p className="text-[9px]">
                  Called {pass.parent_call_data.call_date} at {pass.parent_call_data.call_time} | 
                  Received by: {pass.parent_call_data.who_received} ({pass.parent_call_data.relation_with_student}) | 
                  Response: {pass.parent_call_data.parent_response}
                </p>
              </div>
            )}

            {/* Gate Verification */}
            {pass.gate_verification && (
              <div className="border-t border-emerald-300 pt-1.5 mb-2 bg-emerald-50 -mx-4 px-4 py-2">
                <p className="text-[8px] font-semibold text-emerald-700 uppercase flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> Gate Verification
                </p>
                <p className="text-[9px]">
                  Verified By: {pass.gate_verification.operator_name} | 
                  Time: {new Date(pass.gate_verification.verification_time).toLocaleString("en-IN")}
                  {pass.gate_verification.remarks && ` | Remarks: ${pass.gate_verification.remarks}`}
                </p>
              </div>
            )}

            {/* Only Hostel Warden Signature - hide if gate verified */}
            {pass.status !== "gate_verified" && pass.status !== "closed" && (
            <div className="border-t border-slate-300 pt-3 mt-2">
              <div className="flex justify-end">
                <div className="text-center w-48">
                  <div className="border-b border-slate-400 mb-1 h-8"></div>
                  <p className="text-[9px] text-slate-600 font-medium">Hostel Warden (HW)</p>
                  <p className="text-[7px] text-slate-400">Signature & Date</p>
                </div>
              </div>
            </div>
            )}

            {/* HW + GO Signatures - show when gate verified or closed */}
            {(pass.status === "gate_verified" || pass.status === "closed") && (
            <div className="border-t border-slate-300 pt-3 mt-2">
              <div className="flex justify-end">
                <div className="text-center w-44">
                  <div className="border-b border-slate-400 mb-1 h-8"></div>
                  <p className="text-[9px] text-slate-600 font-medium">Gate Operator (GO)</p>
                  <p className="text-[7px] text-slate-400">Signature & Date</p>
                </div>
              </div>
            </div>
            )}

            {/* Footer */}
            <div className="border-t pt-1.5 mt-2 text-center text-[7px] text-slate-400">
              <p>Printed: {new Date().toLocaleString("en-IN")} | Valid only for mentioned date and time.</p>
              <p className="mt-0.5 font-medium">PIMS Gate Management System - Powered By Steward Healthcare India Pvt Ltd</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page { size: A5 landscape; margin: 5mm; }
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          .gate-pass-print { 
            width: 100% !important; 
            box-shadow: none !important;
            border: 1px solid #ccc;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintGatePass;
